import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { NewTrackerService } from './new-tracker.service';
import {
    DISTINCT_PAGE_SIZE,
    DistinctItemsApi,
    DistinctItemsContext,
    DistinctSelectOption,
    SelectPageState,
    appendUniqueByKey,
    findDistinctItemEntry,
    mapDistinctValues,
    updateHasMore,
} from './distinct-items-pagination.util';

export interface DistinctPageFetchResult {
    batch: DistinctSelectOption[];
    mergedItems: DistinctSelectOption[];
    nextOffset: number;
    hasMore: boolean;
    totalCount: number | null;
}

@Injectable({ providedIn: 'root' })
export class DistinctItemsPaginationService {
    private readonly _trackerService = inject(NewTrackerService);

    fetchPage(
        api: DistinctItemsApi,
        columnKey: string,
        page: SelectPageState,
        reset: boolean,
        context: DistinctItemsContext,
        currentItems: DistinctSelectOption[]
    ): Observable<DistinctPageFetchResult> {
        const nextOffset = reset ? 0 : page.offset + 1;
        const payload = {
            columns: [columnKey],
            table: context.table,
            dataSource: context.dataSource,
            isExternal: context.isExternal,
            offset: nextOffset,
            limit: DISTINCT_PAGE_SIZE,
            keyword: page.keyword ?? '',
        };

        const request$ =
            api === 'filter'
                ? this._trackerService.getDistinctItemsFilters(payload)
                : this._trackerService.getDistinctItems(payload);

        return request$.pipe(
            map((res) => {
                const response = res as {
                    success?: boolean;
                    data?: { data?: Array<Record<string, unknown>> };
                };
                if (!response?.success || !response.data?.data) {
                    return {
                        batch: [] as DistinctSelectOption[],
                        mergedItems: reset ? [] : currentItems,
                        nextOffset: page.offset,
                        hasMore: false,
                        totalCount: page.totalCount,
                    };
                }

                const item = findDistinctItemEntry(
                    response.data.data,
                    columnKey
                ) as { distinct_values?: string[]; total?: number } | undefined;
                const batch = mapDistinctValues(item?.distinct_values ?? []);
                const base = reset ? [] : currentItems;
                const previousLoadedLength = base.length;
                const mergedItems = appendUniqueByKey(base, batch, 'id');
                const totalCount =
                    typeof item?.total === 'number' ? item.total : page.totalCount;

                const nextPage: SelectPageState = {
                    ...page,
                    offset: nextOffset,
                    totalCount,
                };
                updateHasMore(
                    nextPage,
                    batch.length,
                    mergedItems.length,
                    DISTINCT_PAGE_SIZE,
                    previousLoadedLength
                );

                return {
                    batch,
                    mergedItems,
                    nextOffset,
                    hasMore: nextPage.hasMore,
                    totalCount,
                };
            })
        );
    }
}
